package rendering

import common.dto.ElementType
import kotlinx.coroutines.delay
import org.w3c.dom.Node
import three.GLTFLoader
import three.js.Object3D
import kotlin.time.Duration.Companion.milliseconds

object ModelManager {
    private val gltfLoader = GLTFLoader()
    private val models = mutableMapOf<ElementType, Object3D>()

    /**
     * Load the models for all element types. Suspends until all models are loaded.
     *
     * The model file is expected to be located at `models/<name>.glb`. Multiple element
     * types may use the same glb model but apply different modifications to the model
     * (in [ElementType.loadModel]).
     *
     * Thus, we group the element types by name and only load the model once for each name.
     */
    private suspend fun loadModels(
        onError: (dynamic) -> Unit
    ) {
        var loaded = 0
        ElementType.entries.groupBy(ElementType::modelFile).forEach { (modelFile, elementTypes) ->
            val path = "/models/$modelFile.glb"
            gltfLoader.load(path, {
                elementTypes.forEach { elementType ->
                    elementType.loadModel(it.scene.clone())
                    loaded++
                }
                println("Loaded model at $path")
            }, {}, {
                onError(it)
            })
        }
        while (loaded < ElementType.entries.size) {
            delay(100.milliseconds)
        }
    }

    /**
     * Try to load the models via [loadModels].
     *
     * Display a loading message while loading. If an error occurs, panic.
     */
    suspend fun tryLoadModels(loadingElt: Node) {
        // Loading
        loadModels {
            loadingElt.textContent = "Error loading models: $it"
            error("Error loading models: $it") // todo: how does this look like in production?
        }
        println("Models loaded")
    }
}